import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';
import { auth } from '../utils/api';
import { SystemAuctionsManager } from '../features/admin/SystemAuctionsManager';
import { AuctionHousesManager } from '../features/admin/AuctionHousesManager';
import PcgsCacheManagerPage from './Admin/PcgsCacheManagerPage';
import SystemAuctionLotsManagerPage from './Admin/SystemAuctionLotsManagerPage';
import { formatDate } from '../utils/dateUtils';

const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-full">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
  </div>
);

const ErrorDisplay = ({ error }) => (
  <div className="p-4">
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
      {error}
    </div>
  </div>
);

const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiStats, setApiStats] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userDataToDelete, setUserDataToDelete] = useState(null);
  const [showDeleteDataModal, setShowDeleteDataModal] = useState(false);
  const [deleteDataError, setDeleteDataError] = useState(null);
  const [pcgsCheckLoading, setPcgsCheckLoading] = useState(false);
  const [pcgsCheckResult, setPcgsCheckResult] = useState(null);
  const [pcgsCheckError, setPcgsCheckError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  const handlePcgsStatusCheck = async () => {
    setPcgsCheckLoading(true);
    setPcgsCheckError(null);
    setPcgsCheckResult(null);
    
    try {
      const result = await auth.checkAllPCGSStatus();
      setPcgsCheckResult({
        message: result.message
      });
    } catch (err) {
      setPcgsCheckError(err.message);
    } finally {
      setPcgsCheckLoading(false);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  const handleDeleteDataClick = (user) => {
    setUserDataToDelete(user);
    setShowDeleteDataModal(true);
    setDeleteDataError(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      await auth.deleteUser(userToDelete.id);
      setUsers(users.filter(u => u.id !== userToDelete.id));
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (err) {
      setDeleteError(err.message);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setDeleteError(null);
  };

  const handleDeleteDataCancel = () => {
    setShowDeleteDataModal(false);
    setUserDataToDelete(null);
    setDeleteDataError(null);
  };

  const handleDeleteDataConfirm = async () => {
    try {
      await auth.deleteUserData(userDataToDelete.id);
      // Refresh user list to update counts
      const response = await auth.getAllUsers();
      setUsers(response);
      setShowDeleteDataModal(false);
      setUserDataToDelete(null);
    } catch (err) {
      setDeleteDataError(err.message);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user || user.role !== 'admin') return;
      
      try {
        // Get users first
        const usersResponse = await auth.getAllUsers();
        setUsers(usersResponse);

        // Try to get stats, but don't fail if unavailable
        try {
          const statsResponse = await fetch('/api/certification-services/pcgs/stats');
          if (statsResponse.ok) {
            const statsData = await statsResponse.json();
            setApiStats(statsData);
          }
        } catch (err) {
          console.warn('Failed to fetch PCGS stats:', err);
          // Don't set error state - stats are optional
        }
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchData();
    }
  }, [user, authLoading]);

  // Show loading state while checking authentication
  if (authLoading || loading) {
    return <LoadingSpinner />;
  }

  // Redirect non-admin users
  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  return (
    <div className="p-6 flex flex-col h-screen">
      <h1 className="text-2xl font-bold mb-6">Admin Panel</h1>
      <div className="flex flex-1 min-h-0">
        {/* Vertical Tab Navigation */}
        <div className="w-1/6 pr-8">
          <nav className="flex flex-col space-y-2" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('general')}
              className={`${
                activeTab === 'general'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              } group flex items-center px-3 py-2 text-sm font-medium rounded-md`}
            >
              General
            </button>
            <button
              onClick={() => setActiveTab('pcgs-cache')}
              className={`${
                activeTab === 'pcgs-cache'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              } group flex items-center px-3 py-2 text-sm font-medium rounded-md`}
            >
              PCGS Cache Manager
            </button>
            <button
              onClick={() => setActiveTab('system-auction-lots')}
              className={`${
                activeTab === 'system-auction-lots'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              } group flex items-center px-3 py-2 text-sm font-medium rounded-md`}
            >
              System Auction Lots
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="w-5/6 flex flex-col">
          {activeTab === 'general' && (
            <div className="overflow-y-auto">
              {/* PCGS Controls & Statistics */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">PCGS Management</h2>
                  <button
                    onClick={handlePcgsStatusCheck}
                    disabled={pcgsCheckLoading}
                    className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                      pcgsCheckLoading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {pcgsCheckLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Checking...
                      </>
                    ) : (
                      'Check All PCGS Submissions'
                    )}
                  </button>
                </div>

                {pcgsCheckError && (
                  <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    {pcgsCheckError}
                  </div>
                )}

                {pcgsCheckResult && (
                  <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                    {pcgsCheckResult.message}
                  </div>
                )}

                {apiStats && (
                  <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-500">Last 24 Hours</p>
                      <p className="mt-1 text-3xl font-semibold text-gray-900">{apiStats.last_day}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-500">Last 7 Days</p>
                      <p className="mt-1 text-3xl font-semibold text-gray-900">{apiStats.last_week}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-500">Last 30 Days</p>
                      <p className="mt-1 text-3xl font-semibold text-gray-900">{apiStats.last_month}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Auction Houses Management */}
              <div className="mb-8">
                <AuctionHousesManager />
              </div>

              {/* System Auctions Management */}
              <div className="mb-8">
                <SystemAuctionsManager />
              </div>

              {/* Users Table */}
              <h2 className="text-xl font-semibold mb-4">Users</h2>
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  {/* ... table head ... */}
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Login
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Action
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Coins
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Clients
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transactions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.first_name} {user.last_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.role}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(user.created_at, { user })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.last_login ? formatDate(user.last_login, { user, includeTime: true }) : 'Never'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.last_action ? formatDate(user.last_action, { user, includeTime: true }) : 'Never'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <button
                            onClick={() => navigate(`/inventory?userId=${user.id}`)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            {user.coin_count} coins
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <button
                            onClick={() => navigate(`/clients?userId=${user.id}`)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            {user.client_count} clients
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <button
                            onClick={() => navigate(`/transactions?userId=${user.id}`)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            {user.transaction_count} transactions
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.role !== 'admin' && (
                            <div className="space-x-4">
                              <button
                                onClick={() => handleDeleteDataClick(user)}
                                className="text-orange-600 hover:text-orange-900"
                              >
                                Delete Data
                              </button>
                              <button
                                onClick={() => handleDeleteClick(user)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Delete Account
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'pcgs-cache' && <PcgsCacheManagerPage />}
          {activeTab === 'system-auction-lots' && <SystemAuctionLotsManagerPage />}
        </div>
      </div>

      {/* Delete Data Confirmation Modal */}
      {showDeleteDataModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete User Data
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete all data for {userDataToDelete?.first_name} {userDataToDelete?.last_name}? This will permanently delete all their coins, transactions, clients, and preferences. Their account will remain active but empty. This action cannot be undone.
                      </p>
                      {deleteDataError && (
                        <p className="mt-2 text-sm text-red-600">
                          {deleteDataError}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteDataConfirm}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Delete Data
                </button>
                <button
                  type="button"
                  onClick={handleDeleteDataCancel}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete User Account
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete {userToDelete?.first_name} {userToDelete?.last_name}'s account? This will permanently delete all their coins, transactions, and client data. This action cannot be undone.
                      </p>
                      {deleteError && (
                        <p className="mt-2 text-sm text-red-600">
                          {deleteError}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={handleDeleteCancel}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
